import { Box } from "@mui/material";
import { useRef } from "react";
import { useAiChatActions, useAiChatState } from "../AiChatContext";
import { useContractFilter } from "../../contract/ContractFilter.hooks";
import { ContractFilterRequest, transformFilterOptionsToBackend } from "../../contract/ContractFilter.types";
import { FilterOption } from "../../common/components/filterPopover/FilterPopover.types";
import { useFilterBarData } from "../../common/components/filterBar/FilterBar.hooks";
import FilterValueChips from "../../common/components/filterBar/FilterValueChips";
import { isFilterChipDisplayed, nextStoredFilterData, pinnedFilterIdsKey } from "@common/components/filterBar/filterPinning.ts";
import { IdNamePair } from "../../customLists/customListView/CustomListView.types";

interface AiChatScopeChipsProps {
    conversationId: string | null;
}

function AiChatScopeChips({ conversationId }: AiChatScopeChipsProps) {
    const { getScope } = useAiChatState();
    const { setScope } = useAiChatActions();
    const scope = getScope(conversationId);
    const { contractFilterOptions, isLoading } = useContractFilter(scope?.contracts);
    const { data: filterBarData, setQueryData } = useFilterBarData(contractFilterOptions, !isLoading);
    const pinnedFilterIds = scope?.contracts?.pinnedFilterIds;
    // FilterValueChips reports the new values and the new pins through two separate callbacks in the same event, so
    // the pin write cannot read the scope off this render - it would undo the value write that just happened. The ref
    // hands it the blob the value write produced instead.
    const latestFilterRef = useRef<Partial<ContractFilterRequest>>();

    if (!contractFilterOptions.some((o) => isFilterChipDisplayed(o, pinnedFilterIds))) return null;

    function writeScope(filter: Partial<ContractFilterRequest>, ids: Array<string> | undefined) {
        latestFilterRef.current = filter;
        const contracts = nextStoredFilterData(filter, ids) as Partial<ContractFilterRequest>;

        // A scope that is nothing but pins still has to persist: the empty chips are the user's half-finished scope.
        setScope(conversationId, isEmptyFilter(filter) && ids === undefined ? undefined : { contracts });
    }

    function persist(updatedOptions: Array<FilterOption>) {
        writeScope(transformFilterOptionsToBackend(updatedOptions), pinnedFilterIds);
    }

    function handlePinnedFilterIdsChange(ids: Array<string> | undefined) {
        writeScope(latestFilterRef.current ?? scope?.contracts ?? {}, ids);
    }

    function handleSave(updatedOptions: Array<FilterOption>, newFilterBarData: Record<string, IdNamePair[]>) {
        persist(updatedOptions);
        setQueryData(updatedOptions, newFilterBarData);
    }

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pb: 1 }}>
            <FilterValueChips
                filterOptions={contractFilterOptions}
                filterBarData={filterBarData}
                onSave={handleSave}
                onRemove={persist}
                pinnedFilterIds={pinnedFilterIds}
                onPinnedFilterIdsChange={handlePinnedFilterIdsChange}
                popoverPlacement="top"
            />
        </Box>
    );
}

function isEmptyFilter(filter: Partial<ContractFilterRequest>): boolean {
    const ignored = new Set(["pageNumber", "pageSize", "sortByColumn", "sortDirection", "sortByCustomFieldId", pinnedFilterIdsKey]);
    return !Object.entries(filter).some(([key, value]) => {
        if (ignored.has(key) || value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.trim().length > 0;
        if (typeof value === "object") {
            if ("value" in value) {
                const inner = (value as { value: unknown }).value;
                if (Array.isArray(inner)) return inner.length > 0;
                if (typeof inner === "string") return inner.trim().length > 0;
                return inner !== null && inner !== undefined;
            }
            return Object.keys(value).length > 0;
        }
        return Boolean(value);
    });
}

export default AiChatScopeChips;
